import { Button } from '@/components/ui/button'
import { Loader2, Save, X } from 'lucide-react'
import { useMobile } from '@/hooks/use-mobile'
import { useReadOnly } from '@/hooks/use-read-only'
import { cn } from '@/lib/utils'

interface EditActionBarProps {
  /** When true the bar is shown; otherwise it renders nothing. */
  isEditMode: boolean
  onSave: () => void
  onCancel: () => void
  isSaving?: boolean
  saveLabel?: string
}

/**
 * Sticky Save/Cancel bar shown while a detail page is in edit mode, so the
 * controls are always visible without scrolling back to the header
 * (Salesforce-style). Mirrors BulkActionsBar's fixed positioning.
 */
export function EditActionBar({
  isEditMode,
  onSave,
  onCancel,
  isSaving = false,
  saveLabel = 'Save',
}: EditActionBarProps) {
  const isMobile = useMobile()
  const isReadOnly = useReadOnly()

  if (!isEditMode) return null

  return (
    <div
      role="toolbar"
      aria-label="Edit actions"
      className={cn(
        'fixed z-50 bg-card border border-border shadow-lg rounded-lg animate-in slide-in-from-bottom-2',
        isMobile
          ? 'bottom-0 left-0 right-0 rounded-t-lg rounded-b-none px-4 py-3'
          : 'bottom-6 left-1/2 transform -translate-x-1/2 px-6 py-4'
      )}
    >
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground hidden sm:inline" aria-live="polite">
          Unsaved changes
        </span>
        {/* Grackle Raspberry (the --destructive brand token) for prominence —
            color only, not destructive semantics. */}
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
          className="border-destructive text-destructive hover:bg-destructive/10"
        >
          <X className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={onSave}
          disabled={isSaving || isReadOnly}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {isSaving ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
          )}
          {isSaving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </div>
  )
}
